import { Reader } from './bytes';
import type { IndexedFrame } from './image';

export interface Dc6 {
  directions: number;
  framesPerDirection: number;
  /** Frames in file order: direction-major. */
  frames: IndexedFrame[];
}

/**
 * DC6: uncompressed-ish sprite sheets used for UI, fonts, inventory art and some world sprites.
 * Each frame is bottom-up RLE: `0x80` ends a scanline, a byte with the high bit set skips
 * `b & 0x7f` transparent pixels, any other byte is a literal run of that many palette indices.
 * The stored (offsetX, offsetY) locate the frame's bottom-left corner relative to its anchor.
 */
export function decodeDc6(data: Uint8Array): Dc6 {
  const r = new Reader(data);
  const version = r.i32();
  if (version !== 6) throw new Error(`dc6: bad version ${version}`);
  r.skip(12); // flags, encoding, termination
  const directions = r.i32();
  const framesPerDirection = r.i32();
  const n = directions * framesPerDirection;
  const pointers: number[] = [];
  for (let i = 0; i < n; i++) pointers.push(r.u32());
  const frames: IndexedFrame[] = pointers.map((p) => decodeFrame(data, p));
  return { directions, framesPerDirection, frames };
}

function decodeFrame(data: Uint8Array, p: number): IndexedFrame {
  const r = new Reader(data, p);
  const flipped = r.u32();
  const width = r.u32();
  const height = r.u32();
  const ox = r.i32();
  const oy = r.i32();
  r.skip(8); // unknown, next block
  const length = r.u32();
  const pixels = new Uint8Array(width * height);
  let x = 0;
  let y = flipped ? 0 : height - 1;
  const dy = flipped ? 1 : -1;
  let q = r.pos;
  const end = Math.min(data.length, q + length);
  while (q < end) {
    const b = data[q++];
    if (b === 0x80) {
      x = 0;
      y += dy;
    } else if (b & 0x80) {
      x += b & 0x7f;
    } else {
      if (y >= 0 && y < height) {
        const row = y * width;
        for (let i = 0; i < b && x + i < width; i++) pixels[row + x + i] = data[q + i];
      }
      x += b;
      q += b;
    }
  }
  return { width, height, offsetX: ox, offsetY: oy - height, pixels };
}

/**
 * Many full-screen UI pictures are stored as a grid of ≤256×256 tiles, one per frame, laid out
 * row-major. Stitches them into one image (`columns` tiles per row is derived from widths).
 */
export function stitchDc6(frames: IndexedFrame[], columns?: number): IndexedFrame {
  if (frames.length === 1) return { ...frames[0], offsetX: 0, offsetY: 0 };
  let cols = columns ?? 0;
  if (!cols) {
    // Tiles in a row are full width (256) until the last, narrower one.
    cols = 1;
    while (cols < frames.length && frames[cols - 1].width === 256) cols++;
  }
  const rows = Math.ceil(frames.length / cols);
  let width = 0;
  for (let c = 0; c < cols; c++) width += frames[c].width;
  let height = 0;
  for (let rr = 0; rr < rows; rr++) height += frames[rr * cols].height;
  const pixels = new Uint8Array(width * height);
  let yy = 0;
  for (let rr = 0; rr < rows; rr++) {
    let xx = 0;
    for (let c = 0; c < cols; c++) {
      const f = frames[rr * cols + c];
      if (!f) continue;
      for (let y = 0; y < f.height; y++) pixels.set(f.pixels.subarray(y * f.width, (y + 1) * f.width), (yy + y) * width + xx);
      xx += f.width;
    }
    yy += frames[rr * cols].height;
  }
  return { width, height, offsetX: 0, offsetY: 0, pixels };
}
